import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api/axios';
import assetUrl from '../api/assetUrl';
import { useNotify, errorMessage } from '../components/Toast';
import { IconEdit, IconImages, IconTrash, IconCheck } from '../components/Icons';
import '../components/AdminUI.css';
import './Common.css';

// Must match the products.genre / products.type enums in gs_optical.sql.
const GENRE_OPTIONS = [
  { value: 'mixed', label: 'Mixte' },
  { value: 'male', label: 'Homme' },
  { value: 'female', label: 'Femme' },
  { value: 'child', label: 'Enfant' },
];
const GENRE_LABELS = Object.fromEntries(GENRE_OPTIONS.map((o) => [o.value, o.label]));

const TYPE_OPTIONS = [
  { value: 'optical', label: 'Optique' },
  { value: 'sunglass', label: 'Lunettes de soleil' },
  { value: 'uv', label: 'Protection UV' },
  { value: 'lens', label: 'Verres / lentilles' },
  { value: 'accessory', label: 'Accessoire' },
];

const FORM_OPTIONS = [
  { value: '', label: '—' },
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'oval', label: 'Ovale' },
  { value: 'round', label: 'Ronde' },
  { value: 'square', label: 'Carrée' },
  { value: 'cat-eye', label: 'Œil de chat' },
  { value: 'aviator', label: 'Aviateur' },
];

const Products = () => {
  const notify = useNotify();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    ref: '',
    marque: '',
    marque_nom: '',
    pv: '',
    qntt: '',
    couleur: '',
    matiere: '',
    form: '',
    genre: '',
    verre: '',
    pont: '',
    branches: '',
    fournisseur: '',
    os: ''
  });
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageProduct, setImageProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  // Images staged in the create/edit modal before the product exists on the
  // server. Each entry is { file, url } where url is an object URL for preview.
  const [pendingImages, setPendingImages] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [currentPage]);

  // Brand suggestions for the form's datalist, derived from existing products.
  const brandSuggestions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.marque_nom) set.add(p.marque_nom.trim());
    });
    return Array.from(set).sort();
  }, [products]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/products', {
        params: { search: searchTerm, page: currentPage, per_page: 15 },
      });
      setProducts(response.data.data || []);
      setPagination({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        total: response.data.total,
        per_page: response.data.per_page
      });
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- Staged (pending) images in the create/edit modal ---
  const addPendingImages = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    setPendingImages((prev) => [
      ...prev,
      ...files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  };

  const removePendingImage = (index) => {
    setPendingImages((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearPendingImages = () => {
    setPendingImages((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
  };

  const emptyForm = { ref: '', marque: '', marque_nom: '', pv: '', qntt: '', couleur: '', matiere: '', form: '', genre: '', verre: '', pont: '', branches: '', fournisseur: '', os: '' };

  const openCreateModal = () => {
    clearPendingImages();
    setEditingProduct(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const closeProductModal = () => {
    clearPendingImages();
    setShowModal(false);
    setEditingProduct(null);
    setFormData(emptyForm);
  };

  const uploadStagedImages = async (productId) => {
    if (!pendingImages.length || !productId) return;
    const fd = new FormData();
    pendingImages.forEach((p) => fd.append('images[]', p.file));
    // No manual Content-Type — axios adds the multipart boundary itself.
    await axios.post(`/products/${productId}/images`, fd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSavingProduct(true);
    try {
      let productId;
      if (editingProduct) {
        await axios.put(`/products/${editingProduct.id}`, formData);
        productId = editingProduct.id;
      } else {
        const { data } = await axios.post('/products', formData);
        productId = data.product?.id;
      }

      // Upload any images staged in the modal now that we have a product id.
      if (pendingImages.length) {
        try {
          await uploadStagedImages(productId);
        } catch (imgErr) {
          // The product was saved; only the images failed. Surface it clearly
          // so the user can retry via the image manager instead of losing data.
          notify.error(errorMessage(imgErr, 'Produit enregistré, mais échec du téléversement des images'));
          closeProductModal();
          fetchProducts();
          return;
        }
      }

      notify.success(editingProduct ? 'Produit mis à jour' : 'Produit créé');
      closeProductModal();
      fetchProducts();
    } catch (error) {
      notify.error(errorMessage(error, "Erreur lors de l'enregistrement du produit"));
    } finally {
      setSavingProduct(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchProducts();
  };

  const handleEdit = (product) => {
    clearPendingImages();
    setEditingProduct(product);
    setFormData(product);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this product?')) {
      try {
        await axios.delete(`/products/${id}`);
        fetchProducts();
      } catch (error) {
        notify.error(errorMessage(error, 'Erreur lors de la suppression du produit'));
      }
    }
  };

  const handleManageImages = async (product) => {
    setImageProduct(product);
    setShowImageModal(true);
    try {
      const response = await axios.get(`/products/${product.id}`);
      setProductImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images[]', files[i]);
    }

    setUploadingImages(true);
    try {
      // IMPORTANT: do NOT set Content-Type manually. Passing
      // 'multipart/form-data' without a boundary makes the server unable to
      // parse the body (files arrive empty). Let axios derive the header —
      // it appends the required boundary automatically for FormData.
      const { data } = await axios.post(`/products/${imageProduct.id}/images`, formData);
      setProductImages(data.images || []);
      notify.success('Images téléversées');
      e.target.value = '';
      fetchProducts(); // refresh table thumbnails
    } catch (error) {
      e.target.value = '';
      notify.error(errorMessage(error, 'Erreur lors du téléversement des images'));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSetMainImage = async (imageId) => {
    try {
      const { data } = await axios.post(`/products/${imageProduct.id}/images/${imageId}/set-main`);
      setProductImages(data.images || []);
      fetchProducts(); // refresh table thumbnail
    } catch (error) {
      notify.error(errorMessage(error, "Erreur lors de la définition de l'image principale"));
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Supprimer cette image?')) {
      try {
        const { data } = await axios.delete(`/products/${imageProduct.id}/images/${imageId}`);
        setProductImages(data.images || []);
        fetchProducts(); // refresh table thumbnail
      } catch (error) {
        notify.error(errorMessage(error, "Erreur lors de la suppression de l'image"));
      }
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-left">
          <h2>Produits</h2>
          <p>Gérer le catalogue produits</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Nouveau produit
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Rechercher un produit…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button className="btn btn-secondary" onClick={handleSearch}>Rechercher</button>
      </div>

      {loading ? (
        <div className="loading-state">Chargement…</div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Référence</th>
                <th>Marque</th>
                <th>Genre</th>
                <th>Prix</th>
                <th>Stock</th>
                <th>Couleur</th>
                <th>Matière</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    {product.main_image_url ? (
                      <img
                        src={assetUrl(product.main_image_url)}
                        alt={product.ref}
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px'
                      }}>
                        No Image
                      </div>
                    )}
                  </td>
                  <td><strong>{product.ref}</strong></td>
                  <td>{product.marque_nom || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td>{GENRE_LABELS[product.genre] || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td>{product.pv}</td>
                  <td>
                    <span className={`status-badge ${product.qntt > 5 ? 'active' : product.qntt > 0 ? 'warning' : 'inactive'}`}>
                      {product.qntt}
                    </span>
                  </td>
                  <td>{product.couleur}</td>
                  <td>{product.matiere}</td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-icon-btn" title="Modifier" aria-label="Modifier" onClick={() => handleEdit(product)}>
                        <IconEdit />
                      </button>
                      <button className="admin-icon-btn is-primary" title="Gérer les images" aria-label="Gérer les images" onClick={() => handleManageImages(product)}>
                        <IconImages />
                      </button>
                      <button className="admin-icon-btn is-danger" title="Supprimer" aria-label="Supprimer" onClick={() => handleDelete(product.id)}>
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span style={{ padding: '8px 16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
            Page {pagination.current_page} of {pagination.last_page} ({pagination.total} total)
          </span>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === pagination.last_page}
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(pagination.last_page)}
            disabled={currentPage === pagination.last_page}
          >
            Last
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeProductModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</h3>
              <button className="close-btn" onClick={closeProductModal}>&times;</button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Référence *</label>
                  <input
                    value={formData.ref || ''}
                    onChange={(e) => setFormData({ ...formData, ref: e.target.value })}
                    placeholder="ex. GU00193"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Marque</label>
                  <input
                    list="brand-suggestions"
                    value={formData.marque_nom || ''}
                    onChange={(e) => setFormData({ ...formData, marque_nom: e.target.value })}
                    placeholder="ex. Guess, Ray-Ban, Créateur…"
                  />
                  <datalist id="brand-suggestions">
                    {brandSuggestions.map((b) => (
                      <option key={b} value={b} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prix (MAD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.pv || ''}
                    onChange={(e) => setFormData({ ...formData, pv: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Stock *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.qntt ?? ''}
                    onChange={(e) => setFormData({ ...formData, qntt: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Genre</label>
                  <select
                    value={formData.genre || ''}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                  >
                    <option value="">— Choisir —</option>
                    {GENRE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.type || 'optical'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Forme</label>
                  <select
                    value={formData.form || ''}
                    onChange={(e) => setFormData({ ...formData, form: e.target.value })}
                  >
                    {FORM_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Couleur</label>
                  <input
                    value={formData.couleur || ''}
                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                    placeholder="ex. Noir, Marron, Bleu…"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Matière</label>
                <input
                  value={formData.matiere || ''}
                  onChange={(e) => setFormData({ ...formData, matiere: e.target.value })}
                  placeholder="ex. Métal, Plastique, Acétate…"
                />
              </div>

              <div className="form-divider">Mesures de la monture (en mm)</div>

              <div className="form-row">
                <div className="form-group">
                  <label>Largeur du verre</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.verre || ''}
                    onChange={(e) => setFormData({ ...formData, verre: e.target.value })}
                    placeholder="ex. 52"
                  />
                </div>
                <div className="form-group">
                  <label>Pont</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.pont || ''}
                    onChange={(e) => setFormData({ ...formData, pont: e.target.value })}
                    placeholder="ex. 18"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Longueur des branches</label>
                <input
                  type="number"
                  min="0"
                  value={formData.branches || ''}
                  onChange={(e) => setFormData({ ...formData, branches: e.target.value })}
                  placeholder="ex. 145"
                />
              </div>

              <div className="form-divider">Informations supplémentaires</div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fournisseur</label>
                  <input
                    value={formData.fournisseur || ''}
                    onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Code interne marque</label>
                  <input
                    value={formData.marque || ''}
                    onChange={(e) => setFormData({ ...formData, marque: e.target.value })}
                    placeholder="(optionnel)"
                  />
                </div>
              </div>

              <div className="form-divider">Images du produit</div>

              <div className="form-group">
                <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                  + Sélectionner des images
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => { addPendingImages(e.target.files); e.target.value = ''; }}
                    style={{ display: 'none' }}
                  />
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                  {editingProduct
                    ? "Ces images seront ajoutées au produit lors de l'enregistrement. Utilisez « Gérer les images » pour réorganiser ou supprimer les images existantes."
                    : "Max 10 images, 5 Mo chacune (JPEG, PNG, GIF, WebP). La première sera l'image principale."}
                </p>

                {pendingImages.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                    gap: '10px',
                    marginTop: '12px'
                  }}>
                    {pendingImages.map((p, idx) => (
                      <div key={p.url} style={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid var(--border-color)',
                        aspectRatio: '1 / 1',
                        background: '#f8fafc'
                      }}>
                        <img
                          src={p.url}
                          alt={`Aperçu ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        {idx === 0 && !editingProduct && (
                          <span style={{
                            position: 'absolute',
                            left: '6px',
                            bottom: '6px',
                            background: 'var(--color-gold, #b8860b)',
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '6px'
                          }}>
                            Principale
                          </span>
                        )}
                        <button
                          type="button"
                          title="Retirer"
                          onClick={() => removePendingImage(idx)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: '22px',
                            height: '22px',
                            border: 'none',
                            borderRadius: '50%',
                            background: 'rgba(15,23,42,0.75)',
                            color: '#fff',
                            fontSize: '15px',
                            lineHeight: '1',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeProductModal} disabled={savingProduct}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={savingProduct}>
                  {savingProduct
                    ? 'Enregistrement…'
                    : editingProduct
                      ? (pendingImages.length ? 'Enregistrer + images' : 'Enregistrer')
                      : (pendingImages.length ? 'Créer + téléverser' : 'Créer le produit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImageModal && imageProduct && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h3>Gérer les Images - {imageProduct.ref}</h3>
              <button className="close-btn" onClick={() => setShowImageModal(false)}>&times;</button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                  {uploadingImages ? 'Téléchargement...' : '+ Ajouter des Images'}
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    style={{ display: 'none' }}
                  />
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                  Max 10 images, 5MB chacune (JPEG, PNG, GIF, WebP)
                </p>
              </div>

              {productImages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                  Aucune image pour ce produit. Ajoutez-en une maintenant!
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '15px'
                }}>
                  {productImages.map((image) => (
                    <div
                      key={image.id}
                      style={{
                        border: '2px solid ' + (image.is_main ? 'var(--color-gold)' : '#e0e0e0'),
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <img
                        src={assetUrl(image.image_path)}
                        alt={`Product ${image.order}`}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{ padding: '10px', backgroundColor: '#f9f9f9' }}>
                        {image.is_main && (
                          <div style={{
                            color: 'var(--color-gold)',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            marginBottom: '8px'
                          }}>
                            ⭐ Image Principale
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {!image.is_main && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleSetMainImage(image.id)}
                              style={{ flex: '1', fontSize: '11px', padding: '6px 10px' }}
                            >
                              Définir comme principale
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteImage(image.id)}
                            style={{ fontSize: '11px', padding: '6px 10px' }}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
